import { invert } from "lodash-es";

import type { MediumName } from "shared/helpers/media";
import type { Nullable, SentimentRating } from "shared/types";
import {
  type LinkedinPostReactions,
  type SocialInstagramCommentSource,
  type SocialLinkedinCommentSource,
  type SocialTiktokCommentSource,
} from "shared/types/social";

export enum MentionType {
  article = "article",
  blog_post = "blog_post",
  bluesky_post = "bluesky_post",
  customer_article = "customer_article",
  exorde_reddit_post = "exorde_reddit_post",
  exorde_reddit_post_comment = "exorde_reddit_post_comment",
  external_item = "external_item",
  facebook_post = "facebook_post",
  forum_post = "forum_post",
  instagram_comment = "instagram_comment",
  instagram_post = "instagram_post",
  linkedin_comment = "linkedin_comment",
  linkedin_post = "linkedin_post",
  magazine_article = "magazine_article",
  paper_article = "paper_article",
  podcast_episode = "podcast_episode",
  radio_clip = "radio_clip",
  radio_story = "radio_story",
  reddit_post = "reddit_post",
  reddit_post_comment = "reddit_post_comment",
  tiktok_comment = "tiktok_comment",
  tiktok_video = "tiktok_video",
  tv_caption = "tv_caption",
  tv_logo_appearance = "tv_logo_appearance",
  tv_story = "tv_story",
  tv_super = "tv_super",
  tweet = "tweet",
  youtube_video = "youtube_video",
}

export enum SocialMentionType {
  blog_post = MentionType.blog_post,
  bluesky_post = MentionType.bluesky_post,
  exorde_reddit_post = MentionType.exorde_reddit_post,
  exorde_reddit_post_comment = MentionType.exorde_reddit_post_comment,
  facebook_post = MentionType.facebook_post,
  forum_post = MentionType.forum_post,
  instagram_comment = MentionType.instagram_comment,
  instagram_post = MentionType.instagram_post,
  linkedin_comment = MentionType.linkedin_comment,
  linkedin_post = MentionType.linkedin_post,
  reddit_post = MentionType.reddit_post,
  reddit_post_comment = MentionType.reddit_post_comment,
  tiktok_comment = MentionType.tiktok_comment,
  tiktok_video = MentionType.tiktok_video,
  tweet = MentionType.tweet,
  youtube_video = MentionType.youtube_video,
}

export enum BroadcastMentionType {
  radio_clip = MentionType.radio_clip,
  radio_story = MentionType.radio_story,
  tv_caption = MentionType.tv_caption,
  tv_logo_appearance = MentionType.tv_logo_appearance,
  tv_story = MentionType.tv_story,
  tv_super = MentionType.tv_super,
}

export interface BroadcastProvider {
  name: "kinetiq" | "tveyes";
  supported: boolean;
}

export type CisionBroadcastSource = "iqmedia-broadcast" | "tveyes-broadcast";

export const MENTION_TYPE_LONG_TO_SHORT = {
  [MentionType.article]: "ON",
  [MentionType.blog_post]: "BP",
  [MentionType.bluesky_post]: "BS",
  [MentionType.customer_article]: "CA",
  [MentionType.exorde_reddit_post]: "EP",
  [MentionType.exorde_reddit_post_comment]: "EC",
  [MentionType.external_item]: "EI",
  [MentionType.facebook_post]: "FP",
  [MentionType.forum_post]: "FR",
  [MentionType.instagram_comment]: "IC",
  [MentionType.instagram_post]: "IP",
  [MentionType.linkedin_comment]: "LC",
  [MentionType.linkedin_post]: "LP",
  [MentionType.magazine_article]: "MA",
  [MentionType.paper_article]: "PR",
  [MentionType.podcast_episode]: "PE",
  [MentionType.radio_clip]: "RA",
  [MentionType.radio_story]: "RS",
  [MentionType.reddit_post]: "RP",
  [MentionType.reddit_post_comment]: "RC",
  [MentionType.tiktok_comment]: "TK",
  [MentionType.tiktok_video]: "TT",
  [MentionType.tv_caption]: "TC",
  [MentionType.tv_logo_appearance]: "TL",
  [MentionType.tv_story]: "TV",
  [MentionType.tv_super]: "TS",
  [MentionType.tweet]: "TW",
  [MentionType.youtube_video]: "YV",
} as const satisfies Record<MentionType, string>;

export type ShortMentionType = (typeof MENTION_TYPE_LONG_TO_SHORT)[MentionType];

export const MENTION_TYPE_SHORT_TO_LONG = invert(
  MENTION_TYPE_LONG_TO_SHORT
) as { [key in ShortMentionType]: MentionType };

export type SyndicationKey = `${MentionType}_${number}`;

export type Author =
  | {
      name: string;
    }
  | string;

export type Sentiment = number | string | null;

export type Keywords = {
  [key: string]: number;
};

export type Excerpts = {
  keywords: string[];
  text: string;
};

export type SourceGroup = {
  id: number;
  logo_url: string;
  name: string;
};

export type Source = {
  clips_gap_threshold_seconds?: number;
  domain_authority?: number;
  group?: string;
  id: number;
  location: string;
  logo_url: string;
  name: string;
  origin: string;
  outlet_id?: number;
  outlet_monitoring_only_flag?: boolean;
  outlet_status?: "Active" | "Inactive";
  satirical?: boolean;
  source_group?: SourceGroup;
  tier?: number;
  time_zone: string;
};

export type ImpactScore = {
  grade: "Low" | "Medium" | "High";
  score: number;
};

export type Bookmark = {
  color: string;
  id: number;
  label: string;
  organisation_team_id?: number;
  social_bookmarks_stream: boolean;
  stream_id: number;
};

export type FactmataEnrichment = {
  aggregate_emotionality: string;
  aggregate_fakeness: string;
  aggregate_harmful: string;
  aggregate_spam: string;
  risk_score: string;
};

export type Activity = {
  created_at: string;
  feature_id: number;
  feature_name: string;
  user_id: number;
};

export interface BaseMentionInfluencer {
  id: number;
  name: string;
  opted_type: string;
}

export interface EntitySentiment {
  name: Nullable<string>;
  sentiment: number;
  sentiment_label: string;
  type: Nullable<string>;
}

export interface BaseMention {
  activities?: Activity[];
  advertising_value?: Nullable<number>;
  audience: number;
  bookmarks?: Bookmark[];
  broadcast_provider?: Nullable<BroadcastProvider>;
  cision_source?: Nullable<string>;
  created_at: string;
  entities_sentiment?: EntitySentiment[];
  excerpt?: string;
  excerpts?: Excerpts[];
  factmata_enrichment?: FactmataEnrichment;
  id: number;
  impact_score: ImpactScore;
  influencers?: BaseMentionInfluencer[];
  keywords?: Keywords;
  language_code: Nullable<string>;
  media_id: string;
  medium: MediumName;
  published_at: string;
  removed?: boolean;
  sentiment: Sentiment;
  sentiment_ratings?: SentimentRating[];
  source: Source;
  syndication_key: SyndicationKey;
  time_bundle_key: string;
  timestamp: number;
  timestamp_milliseconds: number;
  type: MentionType;
  ucp_id?: Nullable<string>;
}

export interface Article extends BaseMention {
  authenticated_url: string;
  author: Author;
  author_follower_count?: number;
  body?: string;
  datahub?: boolean;
  datahub_media_url?: Nullable<string>;
  desktop_monthly_visits?: number;
  full_body?: string;
  impact?: number;
  in_app_redirect: boolean;
  internal_link: string;
  international_plus: boolean;
  license: Nullable<string>;
  mobile_monthly_visits?: number;
  og_description: Nullable<string>;
  og_image: Nullable<string>;
  original_language?: string;
  original_url?: string;
  outside_source_retention_period: boolean;
  preview_url: string;
  public_link: string;
  related_article?: PaperArticle;
  requires_authenticated_url: boolean;
  score?: number;
  source_follower_count?: number;
  syndications: ArticleSyndication[];
  title: string;
  translated_body?: string;
  translated_title?: string;
  url: string;
  view_count?: number;
  word_count: number;
}

export interface ArticleSyndication extends Omit<Article, "syndications"> {
  source_name: string;
}

export interface PaperArticle extends BaseMention {
  authenticated_url: string;
  author: Author;
  author_follower_count?: number;
  body?: string;
  datahub: boolean;
  datahub_media_url?: Nullable<string>;
  desktop_monthly_visits?: number;
  expired_article: boolean;
  impact?: number;
  in_app_redirect: boolean;
  internal_link: string;
  license: Nullable<string>;
  mediatrack_international_print: boolean;
  mobile_monthly_visits?: number;
  original_language?: string;
  outside_source_retention_period: boolean;
  page_numbers: string;
  pdf_url?: string;
  permission?: boolean;
  public_link: string;
  public_url: string;
  related_article?: Article;
  requires_allowance_check: boolean;
  requires_authenticated_url: boolean;
  requires_content_warning: boolean;
  score?: number;
  section: string;
  size: number;
  source_follower_count?: number;
  syndications: PaperArticleSyndication[];
  thumbnail_url: string;
  title: string;
  translated_body?: string;
  translated_title?: string;
  url?: boolean;
  view_count?: number;
  word_count: number;
}

export interface PaperArticleSyndication extends Omit<Article, "syndications"> {
  source_name: string;
}

export interface MagazineArticle extends BaseMention {
  authenticated_url: string;
  author: Author;
  author_follower_count?: number;
  expired_article: boolean;
  impact?: number;
  in_app_redirect: boolean;
  internal_link: string;
  license: Nullable<string>;
  mediatrack_international_print: boolean;
  outside_source_retention_period: boolean;
  page_numbers: string;
  pdf_url?: string;
  related_article?: Article;
  requires_allowance_check: boolean;
  requires_authenticated_url: boolean;
  score?: number;
  section: string;
  size: number;
  source_follower_count?: number;
  thumbnail_url: string;
  title: string;
  view_count?: number;
  word_count: number;
}

export type TvFragment = {
  id: number;
  text: string;
  timestamp: number;
};

export interface BroadcastStoryFragment {
  end_time: number;
  id: number;
  speaker: string;
  start_time: number;
  text: string;
}

export type ProgramAiring = {
  current_affairs: boolean;
  id: number;
  name: string;
};

export type TvProgram = {
  id: number;
  name: string;
};

export type MediaRequestStatus =
  | "Cancelled"
  | "Completed"
  | "Error"
  | "In Progress"
  | "Incomplete";

export type MediaRequest = {
  broadcast_provider?: Nullable<BroadcastProvider>;
  created_at: string;
  id: number;
  instant_download: boolean;
  media_url: string;
  origin_keyword: string;
  source_type: string;
  status: MediaRequestStatus;
  thumbnail_url: string;
  transcript?: Nullable<string>;
  user: {
    id: number;
    name: string;
  };
};

export type BasicMediaRequest = Pick<
  MediaRequest,
  "broadcast_provider" | "id" | "status" | "transcript" | "user"
> & {
  media_url: Nullable<string>;
};

export type TvThumbnail = {
  channel_id: number;
  created_at: string;
  timestamp: number;
  url: string;
};

interface BroadcastAudience {
  local_viewership_audience?: Nullable<number>;
  national_viewership_audience?: Nullable<number>;
}

export interface TvCaption extends BaseMention, BroadcastAudience {
  author_follower_count?: number;
  broadcast_media_request?: BasicMediaRequest;
  cision_source?: Nullable<CisionBroadcastSource>;
  datahub: boolean;
  end_time: number;
  fragments: TvFragment[];
  impact?: number;
  location?: string;
  program?: TvProgram;
  program_airing?: ProgramAiring;
  program_exclusion: boolean;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  start_time: number;
  syndications: TvCaptionSyndication[];
  text?: string;
  thumbnails?: TvThumbnail[];
  transcript_restricted?: boolean;
  tv_super_fragments?: TvFragment[];
  tv_super_id?: number;
  tveyes_editor_url: Nullable<string>;
  tveyes_media_request?: MediaRequest;
  tveyes_player_url: Nullable<string>;
  tveyes_slim_player_url: Nullable<string>;
  view_count?: number;
}

export interface TvStory {
  end_time: number;
  excerpt?: string;
  fragments: BroadcastStoryFragment[];
  id: number;
  program_airing?: ProgramAiring;
  source: Source;
  start_time: number;
  text?: string;
  thumbnails?: TvThumbnail[];
  timestamp: number;
  type: MentionType.tv_story;
}

// to facilitate resource -> non resource data mapping when only subset of attributes is the same on both sides
type TvCaptionKeysFromResource =
  | "broadcast_provider"
  | "id"
  | "type"
  | "text"
  | "start_time"
  | "end_time"
  | "program_exclusion"
  | "tveyes_media_request"
  | "tveyes_player_url"
  | "tveyes_slim_player_url"
  | "tveyes_editor_url";
export type TvCaptionFromResource = Pick<
  TvCaption,
  TvCaptionKeysFromResource
> & {
  source: Omit<Source, "origin">;
};

export interface TvChannelClip {
  channel_id: number;
  end_time: number;
  excerpts: Excerpts[];
  id: number;
  keywords: Keywords;
  media_url: string;
  mentionId: number;
  program_airing: ProgramAiring;
  source: Source;
  start_time: number;
  type: MentionType;
}

export interface TvClip extends BaseMention {
  author_follower_count?: number;
  channel_id: number;
  end_time: number;
  impact?: number;
  media_url: string;
  program?: TvProgram;
  program_airing?: ProgramAiring;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  start_time: number;
  view_count?: number;
}
export interface TvCaptionSyndication extends Omit<TvCaption, "syndications"> {
  channel_id: number;
  source_id: number;
  source_name: string;
}

export interface TvSuper extends BaseMention {
  end_time: number;
  fragments: TvFragment[];
  internal_link: string;
  location?: string;
  matches_stream?: boolean;
  program?: TvProgram;
  program_airing?: ProgramAiring;
  program_exclusion: boolean;
  start_time: number;
  syndications: TvSuperSyndication[];
  text?: string;
  thumbnails?: TvThumbnail[];
}

export interface TvSuperSyndication extends Omit<TvCaption, "syndications"> {
  channel_id: number;
  source_id: number;
  source_name: string;
}

export interface TvLogoAppearance extends BaseMention {
  author_follower_count?: number;
  end_time: number;
  impact?: number;
  internal_link: string;
  program?: TvProgram;
  program_airing?: ProgramAiring;
  score?: number;
  source_follower_count?: number;
  start_time: number;
  text?: string;
  thumbnails?: TvThumbnail[];
  view_count?: number;
}

export interface RadioClip extends BaseMention, BroadcastAudience {
  author_follower_count?: number;
  broadcast_media_request?: BasicMediaRequest;
  cision_source?: Nullable<CisionBroadcastSource>;
  datahub: boolean;
  end_time: number;
  exclusions?: string[];
  impact?: number;
  internal_link: string;
  media_url: string;
  original: {
    start_time: number;
    station_id: number;
  };
  program_airing: ProgramAiring;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  start_time: number;
  syndications: RadioClipSyndication[];
  text?: string;
  transcript_restricted?: boolean;
  tveyes_editor_url: Nullable<string>;
  tveyes_media_request?: MediaRequest;
  tveyes_player_url: Nullable<string>;
  tveyes_slim_player_url: Nullable<string>;
  view_count?: number;
}

export interface RadioStory {
  end_time: number;
  excerpt?: string;
  fragments: BroadcastStoryFragment[];
  id: number;
  program_airing?: ProgramAiring;
  source: Source;
  start_time: number;
  text?: string;
  timestamp: number;
  type: MentionType.radio_story;
}

interface ExternalPlaybackProperties {
  requires_content_warning?: boolean;
}

export type ExternalPlaybackMention = ExternalPlaybackProperties &
  (PodcastEpisode | RadioClip | TvCaption);

// to facilitate resource -> non resource data mapping when only subset of attributes is the same on both sides
type RadioClipKeysFromResource =
  | "broadcast_provider"
  | "id"
  | "type"
  | "text"
  | "start_time"
  | "end_time"
  | "ucp_id"
  | "created_at"
  | "audience"
  | "language_code"
  | "tveyes_media_request"
  | "tveyes_player_url"
  | "tveyes_slim_player_url"
  | "tveyes_editor_url";
export type RadioClipFromResource = Pick<
  RadioClip,
  RadioClipKeysFromResource
> & {
  source: Omit<Source, "origin">;
};

export interface RadioClipSyndication extends Omit<RadioClip, "syndications"> {
  is_original?: boolean;
  location?: string;
  source_id: number;
  source_name: string;
  station_id: number;
}

export interface InstagramPostSyndication extends Omit<
  InstagramPost,
  "syndications"
> {
  follower_count: Nullable<number>;
  source_name: Nullable<string>;
}

export enum TweetType {
  original = 0,
  retweet = 1,
  reply = 2,
  quote = 3,
}

export type TweetAsset = {
  duration_in_milliseconds: number;
  id: number;
  media_type: string;
  media_url: string;
  url: string;
};

export interface Tweet extends BaseMention {
  author?: Author;
  author_avatar_image?: string;
  author_city?: string;
  author_country?: string;
  author_follower_count?: number;
  author_gender?: string;
  author_latitude?: number;
  author_longitude?: number;
  author_state?: string;
  author_verified?: boolean;
  body: string;
  body_highlighted?: string;
  favorite_count: number;
  handle?: string;
  hashtags: string[];
  impact: number;
  liked_by_url: string;
  original_tweet?: Tweet;
  profile_description?: string;
  public_link: string;
  quote_count: number;
  quoted_by_url: string;
  reply_count: number;
  retweet_count: number;
  retweeted_by_url: string;
  score?: number;
  source_follower_count?: number;
  status_url: string;
  tweet_assets: TweetAsset[];
  tweet_type: TweetType;
  twitter_handle?: string;
  twitter_username?: string;
  url: string;
  user_mentions: string[];
  username?: string;
  view_count?: number;
}

export type FacebookPostAsset = {
  id: number;
  image_url: string;
  media_type: string;
};

export type FacebookPage = {
  id: number;
  url: string;
};

export interface FacebookPost extends BaseMention {
  author_follower_count: Nullable<number>;
  author_verified: boolean;
  body: string;
  comment_count: number;
  facebook_page?: FacebookPage;
  facebook_post_assets: FacebookPostAsset[];
  impact: number;
  internal_link: string;
  like_count: number;
  links: string[];
  public_link: string;
  score?: number;
  share_count: number;
  source_follower_count?: number;
  url: string;
  view_count?: number;
}

export interface YoutubeVideo extends BaseMention {
  author_follower_count: number;
  comment_cont: number;
  dislike_count: number;
  duration: number;
  embedded_player: string;
  impact?: number;
  internal_link: string;
  like_count: number;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  thumbnail_url: string;
  title: string;
  url: string;
  view_count: number;
  youtube_id: string;
}

export type InstagramPostAsset = {
  id: number;
  image_url: string;
  media_type: string;
};

export interface InstagramPost extends BaseMention {
  author_city?: string;
  author_follower_count?: number;
  author_verified: boolean;
  handle: string;
  image: string;
  impact?: number;
  instagram_post_assets: InstagramPostAsset[];
  internal_link: string;
  media_type: Nullable<string>;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  syndications: InstagramPostSyndication[];
  thumbnail_url: Nullable<string>;
  url: string;
  video_url: string;
  view_count?: number;
}

export interface InstagramComment extends Omit<BaseMention, "impact_score"> {
  author: string;
  author_follower_count?: number;
  body: string;
  impact?: number;
  impact_score?: ImpactScore;
  like_count: number;
  public_link: string;
  reply_count: number;
  sentiment_rating: Nullable<string>;
  source: SocialInstagramCommentSource;
  source_follower_count?: number;
  username: string;
  view_count?: number;
}

export type RedditPostAsset = {
  id: number;
  image_url: string;
};

export interface RedditPost extends BaseMention {
  author_follower_count?: number;
  body_highlighted?: string;
  comments_count: number;
  crossposts_count: number;
  has_body: boolean;
  impact?: number;
  internal_link: string;
  likes_count: number;
  over_18: boolean;
  public_link: string;
  reddit_post_assets: RedditPostAsset[];
  score: number;
  source_follower_count: number;
  source_name: string;
  summary: string;
  title: string;
  url: string;
  view_count?: number;
}

export interface ExordeRedditAuthor {
  username: string;
}

export interface ExordeRedditAuthorMetrics {
  author_comment_count?: number;
  author_comment_karma?: number;
  author_post_count?: number;
  author_post_karma?: number;
}

export interface ExordeRedditPost
  extends ExordeRedditAuthorMetrics, Omit<RedditPost, "author"> {
  author: ExordeRedditAuthor;
  author_verified?: boolean;
  body?: string;
  type: MentionType.exorde_reddit_post;
}

export interface RedditPostComment extends BaseMention {
  author: string;
  author_follower_count?: number;
  body?: string;
  body_highlighted?: string;
  comments_count: number;
  impact?: number;
  internal_link: string;
  likes_count: number;
  over_18: boolean;
  public_link: string;
  reddit_post?: RedditPost;
  score: number;
  source_follower_count?: number;
  source_name: string;
  summary: string;
  url: string;
  view_count?: number;
}

export interface ExordeRedditPostComment
  extends ExordeRedditAuthorMetrics, Omit<RedditPostComment, "author"> {
  author: ExordeRedditAuthor;
  author_verified?: boolean;
  reddit_post?: ExordeRedditPost;
  title: string;
  type: MentionType.exorde_reddit_post_comment;
}

export interface BlogPost extends BaseMention {
  author_follower_count?: number;
  impact?: number;
  internal_link: string;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  title: string;
  url: string;
  view_count?: number;
  word_count?: number;
}

export interface ForumPost extends BaseMention {
  author_follower_count?: number;
  impact?: number;
  internal_link: string;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  title: string;
  url: string;
  view_count?: number;
  word_count?: number;
}

export interface PodcastEpisode extends BaseMention {
  author_follower_count?: number;
  impact?: number;
  internal_link: string;
  public_link: string;
  score?: number;
  source_follower_count?: number;
  text?: string;
  title: string;
  transcript_restricted?: boolean;
  tveyes_player_url: Nullable<string>;
  tveyes_slim_player_url: Nullable<string>;
  url: string;
  view_count?: number;
}

export type BlueskyPostAsset = {
  id: number;
  thumbnail: string;
  type: string;
  url: string;
};

export interface BlueskyPost extends BaseMention {
  assets: BlueskyPostAsset[];
  author_follower_count?: number;
  body?: string;
  hashtags: string[];
  highlighted_text: string;
  likes_count?: number;
  public_link: string;
  reply_count?: number;
  repost_count?: number;
  source_follower_count?: number;
  url: string;
  view_count?: number;
  // Placeholder for: https://app.asana.com/1/913360228200452/project/1209314062985194/task/1209513079035149?focus=true
}

export type LinkedinAsset = {
  media_type: string;
  thumbnail_url?: string;
  url: string;
};

export interface LinkedinPostSyndication extends Omit<
  LinkedinPost,
  "syndications"
> {
  follower_count: Nullable<number>;
  source_name: Nullable<string>;
}

export interface LinkedinPost extends BaseMention {
  assets?: LinkedinAsset[];
  author: string;
  author_follower_count?: number;
  body?: string;
  comment_count?: number;
  like_count?: number;
  product_type?: "owned" | "share" | "mention";
  public_link: string;
  quoted_post?: LinkedinPost;
  reactions: LinkedinPostReactions;
  share_post?: LinkedinPost;
  source_follower_count?: number;
  syndications: LinkedinPostSyndication[];
  url: string;
  view_count?: number;
}

export interface LinkedinComment extends BaseMention {
  assets?: LinkedinAsset[];
  author: string;
  author_follower_count?: number;
  body?: string;
  comment_count?: number;
  like_count?: number;
  public_link: string;
  show_commented?: boolean;
  source: SocialLinkedinCommentSource;
  source_follower_count?: number;
  url: string;
  view_count?: number;
}

export interface TiktokComment extends Omit<
  BaseMention,
  "audience" | "impact_score" | "source"
> {
  audience: Nullable<number>;
  author: string;
  author_follower_count?: number;
  author_verified?: boolean;
  body: string;
  handle?: Nullable<string>;
  impact?: number;
  impact_score?: Nullable<ImpactScore>;
  internal_link?: string;
  like_count: number;
  liked?: boolean;
  owner?: boolean;
  pinned?: boolean;
  product_type?: string;
  profile_image_url?: Nullable<string>;
  public_link: string;
  reply_count: number;
  sentiment_rating?: Nullable<string>;
  source: SocialTiktokCommentSource;
  source_follower_count?: number;
  status?: Nullable<string>;
  url?: string;
  username: string;
  view_count?: number;
}

export type TiktokVideoAsset = {
  id: number;
  thumbnail: string;
  type: string;
  url: string;
};

export interface TiktokVideo extends BaseMention {
  assets: TiktokVideoAsset[];
  author?: string;
  author_follower_count?: number;
  author_verified: boolean;
  body?: string;
  comment_count?: number;
  favourite_count?: number;
  handle?: Nullable<string>;
  hashtags: string[];
  highlighted_text: string;
  like_count?: number;
  product_type?: "hashtag" | "mention" | "own";
  public_link?: string;
  reply_count?: number;
  repost_count?: number;
  share_count?: number;
  source_follower_count?: number;
  url: string;
  username: string;
  view_count?: number;
}

export interface CustomerArticle extends Pick<
  BaseMention,
  "id" | "created_at" | "published_at" | "excerpts" | "type"
> {
  body: string;
  keyword: string;
  medium: string;
  source: string;
  text: string;
  title: string;
  url: string;
  word_count: number;
}

export interface ExternalItem extends Omit<
  BaseMention,
  | "activities"
  | "bookmarks"
  | "excerpt"
  | "factmata_enrichment"
  | "removed"
  | "source"
> {
  author_follower_count?: number;
  created_by: string;
  factmata_enrichment?: FactmataEnrichment;
  impact?: number;
  public_link: string;
  score?: number;
  source: Pick<Source, "name" | "time_zone">;
  source_follower_count?: number;
  summary: string;
  title: string;
  view_count?: number;
}
export type MentionWithSyndication =
  | Article
  | InstagramPost
  | LinkedinPost
  | PaperArticle
  | RadioClip
  | TvCaption
  | TvSuper;

export type Mention =
  | Article
  | BlogPost
  | BlueskyPost
  | ExordeRedditPost
  | ExordeRedditPostComment
  | ExternalItem
  | FacebookPost
  | ForumPost
  | InstagramComment
  | InstagramPost
  | LinkedinComment
  | LinkedinPost
  | MagazineArticle
  | PaperArticle
  | PodcastEpisode
  | RadioClip
  | RadioClipSyndication
  | RedditPost
  | RedditPostComment
  | TiktokComment
  | TiktokVideo
  | TvCaption
  | TvCaptionSyndication
  | TvClip
  | TvLogoAppearance
  | Tweet
  | YoutubeVideo;

export type BroadcastProviderOwner = Pick<Mention, "broadcast_provider">;

export type Syndication =
  | ArticleSyndication
  | InstagramPostSyndication
  | LinkedinPostSyndication
  | PaperArticleSyndication
  | RadioClipSyndication
  | TvCaptionSyndication
  | TvSuperSyndication;

export type SocialMention =
  | BlogPost
  | BlueskyPost
  | ExordeRedditPost
  | ExordeRedditPostComment
  | FacebookPost
  | ForumPost
  | InstagramComment
  | InstagramPost
  | LinkedinComment
  | LinkedinPost
  | RedditPost
  | RedditPostComment
  | TiktokComment
  | TiktokVideo
  | Tweet
  | YoutubeVideo;

export type SocialMentionWithSyndication = InstagramPost | LinkedinPost;

export type SocialSyndication =
  | InstagramPostSyndication
  | LinkedinPostSyndication;

export type MentionOrCustomerArticle = CustomerArticle | Mention;

export type MentionsWithPostAssets =
  | FacebookPost
  | InstagramPost
  | RedditPost
  | BlueskyPost
  | TiktokVideo
  | Tweet;

export type TvEyesMention = (TvCaption | RadioClip | PodcastEpisode) & {
  tveyes_player_url: Nullable<string>;
  tveyes_slim_player_url: Nullable<string>;
};

export const SHARE_TYPES_BY_PLATFORM = {
  facebook: [
    "facebook_shares",
    "facebook_likes",
    "facebook_comments",
    "facebook_haha_count",
    "facebook_love_count",
    "facebook_sad_count",
    "facebook_wow_count",
  ],
  pinterest: ["pinterest_shares"],
  reddit: ["reddit_shares"],
  twitter: ["twitter_shares"],
} as const;

export type ShareTypePlatform = keyof typeof SHARE_TYPES_BY_PLATFORM;

export type ShareType =
  (typeof SHARE_TYPES_BY_PLATFORM)[ShareTypePlatform][number];
